import { PaymentTabsEnum } from '../../cart-bridger-pay/enums/payment-tabs-enum';
import { CreditCardTabTabsEnum } from '../../cart-bridger-pay/enums/credit-card-tab';
import { IFormControl } from '../../../../ui/forms/interfaces';
import { scrollToFirstErrorControl, validateForm } from '../../../../domain/validation/validation';
import {
    IConfirmOrderNewCreditCardValidation,
    IConfirmOrderSavedCreditCardValidation
} from '../../cart-bridger-pay/interfaces';
import {
    BRIDGER_PAY_VALIDATION_EVENT,
    sendCartEvent
} from '../../cart-bridger-pay/domain/shopping-cart-events-provider';
import { getHiddenDataBoolean, getIsChina } from '../../../../domain/hidden-data-provider';
import { removeCartDataStorage } from '../../cart-bridger-pay/data/cart-storage-provider';
import { DEFAULT_ERROR } from '../../../../DAL/data-provider';

export const handleCardHolderNameValidation = (
    selectedPaymentTab: PaymentTabsEnum,
    selectedCreditCardTab: CreditCardTabTabsEnum,
    isZeroPurchase: boolean,
    nameOnCardControl: IFormControl,
    updateNameOnCardControl: (updatedControl: IFormControl) => void
) => {
    if(isZeroPurchase ||
        selectedPaymentTab !== PaymentTabsEnum.CreditCard ||
        selectedCreditCardTab !== CreditCardTabTabsEnum.NewCreditCard) return true;

    const newCreditCardsErrorControls: IFormControl[] = [];
    const isNewCreditCardFormValid = validateForm([
        [
            nameOnCardControl,
            (updatedControl: IFormControl) => {
                updateNameOnCardControl(updatedControl)
            }
        ]
    ], newCreditCardsErrorControls);

    if(!isNewCreditCardFormValid){
        scrollToFirstErrorControl(newCreditCardsErrorControls);
    }

    return isNewCreditCardFormValid;
};

export const formatBridgerPayError = (error: string|unknown) => {
    const err = (error as string) ?? '';

    // Failed to create BridgerPay transaction
    if(err.trim().toLowerCase().includes('bridgerpay')) {
        return DEFAULT_ERROR;
    }

    return err.toString();
};

export const handleNewCreditCardValidation = async (
    selectedPaymentTab: PaymentTabsEnum,
    selectedCreditCardTab: CreditCardTabTabsEnum,
    isZeroPurchase: boolean,
    isSubscription: boolean,
    bloxController: any,
    rememberMe: boolean,
    cardHolderName: string,
    cvv: string,
    setError: (error: string) => void,
    updateCardToken: (token: string) => void
) : Promise<IConfirmOrderNewCreditCardValidation> => {

    if(isZeroPurchase ||
        selectedPaymentTab !== PaymentTabsEnum.CreditCard ||
        selectedCreditCardTab !== CreditCardTabTabsEnum.NewCreditCard) {
        return {
            isValid: true,
            newCreditCardToken: '',
            encryptedCvv: '',
            cvv: '',
            cardType: '',
            cardChecksum: '',
        };
    }

    if(!bloxController.isAllValid) {
        console.log('not valid')
        bloxController.validateAll();
        sendCartEvent(BRIDGER_PAY_VALIDATION_EVENT, bloxController);
        return {
            isValid: false,
            newCreditCardToken: '',
            encryptedCvv: '',
            cvv: '',
            cardType: '',
            cardChecksum: '',
        };
    }

    const singleUse = !rememberMe;
    let token = '';
    let encryptedCvv = '';
    let isValid = true;
    let serverError = '';
    let cardType = '';
    let cardChecksum = '';

    try {
        // https://developers.bridgerpay.com/docs/blox-card-tokenization-sdk
        // https://developers.bridgerpay.com/docs/blox-encrypt-cvv-sdk
        const bpResult = await bloxController.createPaymentCardToken({
            cardHolderName, // This field is mandatory
            singleUse,
        });

        const sbsDebitAllowed = getHiddenDataBoolean('sbs-debit-allowed');

        if(isSubscription && !sbsDebitAllowed && bpResult?.card?.type === 'debit') {
            setError('Subscriptions cannot be purchased with Debit/Prepaid card.');
            return {
                isValid: false,
                newCreditCardToken: '',
                encryptedCvv: '',
                cvv: '',
                cardType: '',
                cardChecksum: '',
            };
        }

        token = bpResult?.token || '';
        encryptedCvv = bpResult?.encryptedCvv || '';
        cardType = bpResult?.card?.type || '';
        cardChecksum = bpResult?.cardChecksum || '';

        console.log('bpResult', bpResult)
    }
    catch (_error: any) {
        console.log('Create BP card token error', _error);

        if(_error) {
            if(typeof _error === 'string') {
                serverError = formatBridgerPayError(_error);
            }
            else{
                const errorMessages = Object.entries(_error);
                serverError = errorMessages && errorMessages.length > 0 ? `${ formatBridgerPayError(errorMessages[0][0]) }: ${ formatBridgerPayError(errorMessages[0][1]) }` : '';
            }
        }

        isValid = false;
    }

    if(!isValid || !token) {
        scrollToAddNewCardForm();
        bloxController?.validateAll();
        sendCartEvent(BRIDGER_PAY_VALIDATION_EVENT, bloxController);

        return {
            isValid: false,
            newCreditCardToken: '',
            encryptedCvv: '',
            cvv: '',
            serverError,
            cardType: '',
            cardChecksum: '',
        };
    }

    // update new credit card token for the future use
    updateCardToken(token);

    return {
        isValid: true,
        newCreditCardToken: token,
        encryptedCvv: encryptedCvv,
        cvv,
        serverError,
        cardType,
        cardChecksum,
    };
};

export const handleSavedCreditCardsValidation = (
    selectedPaymentTab: PaymentTabsEnum,
    selectedCreditCardTab: CreditCardTabTabsEnum,
    isZeroPurchase: boolean,

    savedCreditCardToken: string,
    savedCreditCardEncryptedCvv: string,
    savedCreditCardKenticoItemID: number,
    cardChecksum: string,

    cvvControl?: IFormControl,
    updateCvvControl?: (updatedControl: IFormControl) => void

) : IConfirmOrderSavedCreditCardValidation => {

    if(isZeroPurchase ||
        selectedPaymentTab !== PaymentTabsEnum.CreditCard ||
        selectedCreditCardTab !== CreditCardTabTabsEnum.SavedCreditCards) {
        return {
            isValid: true,
            savedCreditCardToken: '',
            encryptedCvv: '',
            savedCreditCardKenticoItemID: 0,
            cvv: '',
            cardChecksum: '',
        }
    }

    // If credit card token returned from 'saved credit cards'
    // server response has token & CVV ---> return this data.
    if(savedCreditCardToken &&
        savedCreditCardEncryptedCvv &&
        savedCreditCardKenticoItemID) {
        return {
            isValid: true,
            savedCreditCardToken: savedCreditCardToken,
            encryptedCvv: savedCreditCardEncryptedCvv,
            savedCreditCardKenticoItemID,
            cvv: '',
            cardChecksum,
        };
    }

    // If CVV is not returned from server ---->
    // user must enter it.
    let isValid = !!savedCreditCardToken && !!(cvvControl?.value ?? '');

    if(!isValid && updateCvvControl && cvvControl) {
        updateCvvControl(cvvControl);
        scrollToPath('#cvv-tb');
    }

    return {
        isValid,
        savedCreditCardToken: savedCreditCardToken,
        encryptedCvv: '',
        savedCreditCardKenticoItemID,
        cvv: cvvControl?.value.trim() || '',
        cardChecksum,
    }
};

export const scrollToPath = (path: string) => {
    const $el = document.querySelector(path);
    if(!$el) return;

    $el.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'nearest'
    });
};

const scrollToAddNewCardForm = () => {

    const $el = document.getElementById('nameOnCard');
    if(!$el) return;

    const $header = document.getElementById('site-header');

    const fixed = !!$header ? $header.offsetHeight : 0;
    const top = $el.getBoundingClientRect().top;

    window.scrollTo({
        top: top + fixed
    });

    $el.scrollIntoView(true);
};

export const checkIfPaypalIsVisible = (isSubscription: boolean) => {
    const isChina = getIsChina();
    return !isChina; // && !isSubscription;
};

export const checkIfApplePayIsVisible = (isSubscription: boolean) => {
    const isChina = getIsChina();
    const isApplePayEnabled = getHiddenDataBoolean('apple-pay-enabled');
    return !isChina && isApplePayEnabled;
};

export const getPaymentRootClasses = (isPaypalTabVisible: boolean) => {

    if(!isPaypalTabVisible){
        return 'grid w-1/2';
    }
    else{
        return 'grid grid-cols-2';
    }

    /*
    if(isChina){
        return isSubscription ? 'grid w-1/2' : 'grid grid-cols-2';
    }
    else{
        return isSubscription ? 'grid grid-cols-2' : 'grid grid-cols-3';
    }*/
};

export const redirectToThankYouPage = (orderGUID: string, isInAppPurchase: boolean) => {

    removeCartDataStorage();

    // redirect to the thank-you page;
    // ga=1 means that we need to send 'TransactionTrigger' event in thank you page.
    // this event should be sent only once, so after sending it we remove ga=1 from the query string.
    const qs = orderGUID ? `?oid=${ orderGUID }&ga=1` : '';
    window.location.href = isInAppPurchase ? `/inapp/cart/thank-you${ qs }` : `/cart/thank-you${ qs }`;
    return true;
};
